import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const useSessionTimeout = () => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  
  // Timer references
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  
  // Configuration
  const INACTIVITY_TIMEOUT = 240 * 60 * 1000; // 240 minutes in milliseconds
  const WARNING_TIMEOUT = 2 * 60 * 1000; // 2 minutes in milliseconds
  const COUNTDOWN_INTERVAL = 1000; // 1 second

  // Reset all timers
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    // Reset warning state
    setShowWarning(false);
    setTimeLeft(120);
    
    // Set new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      console.log('🕐 User inactive for 20 minutes - showing warning');
      setShowWarning(true);
      setTimeLeft(120); // Reset countdown to 2 minutes
      
      // Start countdown timer
      countdownTimerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Time's up - logout user
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, COUNTDOWN_INTERVAL);
      
      // Set warning timeout (2 minutes)
      warningTimerRef.current = setTimeout(() => {
        console.log('⏰ Warning timeout reached - logging out');
        handleLogout();
      }, WARNING_TIMEOUT);
      
    }, INACTIVITY_TIMEOUT);
  }, []);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    console.log('👤 User activity detected - resetting timers');
    resetTimers();
  }, [resetTimers]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      console.log('🚪 Session timeout - logging out user');
      
      // Clear all timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      
      // Clear storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Show logout message (persistent toast)
      toast.warning('Session expired due to inactivity. Please login again.', {
        autoClose: false,
        closeOnClick: true,
        draggable: true,
        toastId: 'session-expired'
      });
      
      // Navigate to login
      navigate('/', { replace: true });
      
    } catch (error) {
      console.error('Error during session logout:', error);
      // Force logout even if there's an error
      localStorage.clear();
      sessionStorage.clear();
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Handle user confirmation (user clicked "I'm here" button)
  const handleUserConfirmation = useCallback(() => {
    console.log('✅ User confirmed presence - resetting session');
    toast.success('Session extended successfully!');
    resetTimers();
  }, [resetTimers]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Start the initial timer
    resetTimers();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      // Clear all timers on cleanup
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [handleUserActivity, resetTimers]);

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    showWarning,
    timeLeft,
    formatTime,
    handleUserConfirmation,
    handleLogout
  };
};

export default useSessionTimeout;
